import React from 'react'

// material ui forms
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import FormHelperText from '@mui/material/FormHelperText'

// shared components
import FormConditionalSubgroup from 'components/atoms/FormConditionalSubgroup'
import Typography from '@mui/material/Typography'
import FormTextFieldWithValidation from 'components/shared/FormTextFieldWithValidation'
import FormControlWithErrorHelper from 'components/shared/FormControlWithErrorHelper'

// local components
import AllergiesSeverityInfo from './AllergiesSeverityInfo'
import LegacyAllergiesWarning from './LegacyAllergiesWarning'
import SelectStandardAllergies from './SelectStandardAllergies'
import SelectCustomAllergies from './SelectCustomAllergies'
import SevereFoodAllergyInfo from './SevereFoodAllergyInfo'
import NoNopeWarning from './NoNopeWarning'
import SeatingPreferenceForm from './SeatingPreferenceForm'

import { hasSevereFoodAllergies } from 'lib/helpers/camper/allergies'

const AllergiesForm = props => {
  const {
    // via passed props
    food,
    setModal,

    // via EditCamperContainer:
    form, // via redux state
    allergies, // via withQuery
    formErrors, // via withFormResults

    // via CamperFormContainer:
    onChangeCamperFormField, // via withEventHandlers
    onChangeBooleanStringField, // via withEventHandlers
    onChangeStandardAllergyField, // via withEventHandlers
    onChangeCustomAllergyField // via withEventHandlers
  } = props

  const allergyType = food ? 'food' : 'non-food'
  const toggleField = food ? 'hasFoodAllergies' : 'hasNonFoodAllergies'
  const toggleLabel = food ? 'Food Allergies' : 'Non-Food Allergies'
  const toggleFieldValue = form[toggleField]
  const notesField = food ? 'foodAllergyNotes' : 'nonFoodAllergyNotes'
  const displaySevereFoodAllergyInfo =
    food && hasSevereFoodAllergies(form.camperAllergies)
  const displayNoNopeAllergyWarning = form.noNopeAllergyWarning

  return (
    <>
      {form.hasLegacyAllergies && (
        <LegacyAllergiesWarning
          type={allergyType}
          allergies={form.allergies}
          allergiesOther={form.allergiesOther}
        />
      )}
      <FormControlWithErrorHelper
        component='fieldset'
        required
        errorsObject={formErrors}
        errorField={toggleField}
      >
        <FormLabel htmlFor='allergies' component='legend'>
          {toggleLabel}
        </FormLabel>
        <RadioGroup
          value={String(toggleFieldValue)}
          onChange={onChangeBooleanStringField(toggleField)}
          aria-label={`${allergyType} allergies`}
          name={toggleField}
        >
          <FormControlLabel value='true' control={<Radio />} label='Yes' />
          <FormControlLabel value='false' control={<Radio />} label='No' />
        </RadioGroup>
      </FormControlWithErrorHelper>

      <FormConditionalSubgroup condition={form[toggleField]}>
        <AllergiesSeverityInfo />
        <SelectStandardAllergies
          food={food}
          allergies={allergies}
          form={form}
          onChangeStandardAllergyField={onChangeStandardAllergyField}
        />

        <SelectCustomAllergies
          food={food}
          severe
          allergies={allergies}
          form={form}
          onChangeCustomAllergyField={onChangeCustomAllergyField}
          formErrors={formErrors}
        />

        <SelectCustomAllergies
          food={food}
          severe={false}
          allergies={allergies}
          form={form}
          onChangeCustomAllergyField={onChangeCustomAllergyField}
          formErrors={formErrors}
        />

        <FormConditionalSubgroup condition={displayNoNopeAllergyWarning}>
          <NoNopeWarning field='allergies' />
        </FormConditionalSubgroup>

        <FormConditionalSubgroup condition={displaySevereFoodAllergyInfo}>
          <SevereFoodAllergyInfo />
          <SeatingPreferenceForm
            form={form}
            formErrors={formErrors}
            onChangeBooleanStringField={onChangeBooleanStringField}
            setModal={setModal}
          />
        </FormConditionalSubgroup>

        <FormTextFieldWithValidation
          label={
            <Typography>
              Additional{' '}
              <Typography
                component='span'
                display='inline'
                fontWeight='fontWeightBold'
              >
                {allergyType}
              </Typography>{' '}
              allergy notes
            </Typography>
          }
          form={form}
          field={notesField}
          onChange={onChangeCamperFormField}
          errors={formErrors}
          multiline
        >
          <FormHelperText>
            Explain care or reaction and specify if Epi-Pen is carried
          </FormHelperText>
        </FormTextFieldWithValidation>
      </FormConditionalSubgroup>
    </>
  )
}

export default AllergiesForm
